import { Input } from "@legal-500/legal500-design-system";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { CmsImageUploadField } from "../../../components/CMS/CmsImageUploadField/CmsImageUploadField";
import { RichTextField, useUploadTracker } from "./blockEditorParts";
import { ButtonFields } from "./ButtonFields";
import { buttonOrNothing, emptyButton, withoutEmptySection } from "./landingEditorParts";
import { SectionFields, type SectionFormValues, toSectionFormValues } from "./SectionFields";
import type { BannerContent, BlockButton, BlockContentErrors } from "./types";
import { useContentChange } from "./useContentChange";
import styles from "../CMS.module.css";

type BannerEditorProps = {
  value: BannerContent;
  onChange: (value: BannerContent) => void;
  errors: BlockContentErrors;
  onUploadingChange?: (uploading: boolean) => void;
};

type BannerFormValues = SectionFormValues & {
  title: string;
  tagline: string;
  primaryButton: BlockButton;
  secondaryButton: BlockButton;
  videoUrl: string;
  poster: string;
};

function toContent(initial: BannerContent, values: BannerFormValues): BannerContent {
  const { primaryButton, secondaryButton, ...fields } = values;
  const { primaryButton: _primaryButton, secondaryButton: _secondaryButton, ...rest } = initial;
  const primary = buttonOrNothing(primaryButton);
  const secondary = buttonOrNothing(secondaryButton);
  return {
    ...withoutEmptySection({ ...rest, ...fields, title: fields.title.trim() }, [
      "videoUrl",
      "poster",
    ]),
    ...(primary ? { primaryButton: primary } : {}),
    ...(secondary ? { secondaryButton: secondary } : {}),
  };
}

export function BannerEditor({ value, onChange, errors, onUploadingChange }: BannerEditorProps) {
  const [initial] = useState(value);
  const { control, register, watch } = useForm<BannerFormValues>({
    defaultValues: {
      ...toSectionFormValues(value),
      title: value.title,
      tagline: value.tagline,
      primaryButton: value.primaryButton ?? emptyButton("primary"),
      secondaryButton: value.secondaryButton ?? emptyButton("secondary"),
      videoUrl: value.videoUrl ?? "",
      poster: value.poster ?? "",
    },
  });
  const { report } = useUploadTracker(onUploadingChange);
  useContentChange(watch, onChange, values => toContent(initial, values));

  return (
    <div className={styles.blockEditor}>
      <SectionFields
        register={register}
        idPrefix="banner"
        errors={errors}
        headingLabel="Eyebrow (optional)"
      />

      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor="banner-title">
          Title <span className={styles.required}>*</span>
        </label>
        <Input id="banner-title" error={errors.title} {...register("title")} />
      </div>

      <RichTextField
        control={control}
        name="tagline"
        id="banner-tagline"
        label="Tagline"
        minHeight={120}
        emptyAsBlank
      />

      <ButtonFields
        register={register}
        name="primaryButton"
        idPrefix="banner-primary-button"
        legend="Primary button (optional)"
        errors={errors}
      />
      <ButtonFields
        register={register}
        name="secondaryButton"
        idPrefix="banner-secondary-button"
        legend="Secondary button (optional)"
        errors={errors}
      />

      <fieldset className={styles.fieldset}>
        <legend className={styles.fieldsetLegend}>Background video (optional)</legend>
        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="banner-video-url">
            Vimeo link
          </label>
          <Input
            id="banner-video-url"
            placeholder="https://vimeo.com/123456789"
            error={errors.videoUrl}
            {...register("videoUrl")}
          />
        </div>
        <Controller
          name="poster"
          control={control}
          render={({ field }) => (
            <CmsImageUploadField
              value={field.value}
              onChange={field.onChange}
              onUploadingChange={pending => report("poster", pending)}
              label="Poster image"
              noun="poster"
              cardLabel="Banner"
              previewAlt="Banner poster"
              optional={watch("videoUrl").trim() === ""}
              guidance="JPG, PNG, GIF or WebP, up to 20 MiB. Required with a background video; shown on narrow screens and to visitors who prefer reduced motion."
            />
          )}
        />
        {errors.poster && (
          <p className={styles.fieldError} role="alert">
            {errors.poster}
          </p>
        )}
      </fieldset>
    </div>
  );
}
